import ReactGA from 'react-ga4';

// Module-level flag controlling whether analytics events are sent.
// Disabled when the logged-in user is on the blocklist or is actively
// proxying as another manager.
let _analyticsEnabled = true;

/**
 * Enable or disable analytics event emission for the current session.
 * Call with `false` when a superuser/admin selects another manager,
 * and with `true` when that selection is cleared.
 */
export const setAnalyticsEnabled = (enabled: boolean): void => {
  _analyticsEnabled = enabled;
};

// Analytics Event Names - Using abbreviations to shorten names while maintaining clarity
//
// Abbreviations used:
// - MA = Manager Assistant
// - FA = Feedback Assistant
// - CF = Career Frameworks
// - MF = Module Feedback
//
// Format: [Section Abbr] - [Subsection] - [Action]
// Max length: 31 characters (same as "Feedback Assistant - Time Spent")
//
// NOTE: "Prev Review" event values are kept as-is for analytics continuity,
// even though the tab was renamed from "Previous Review" to "Manager Review".

// Manager Assistant Events
export const ANALYTICS_EVENTS = {
  MANAGER_ASSISTANT: {
    FETCH_FEEDBACK_DATA: 'MA - Fetch Feedback Data',
    TIME_SPENT: 'MA - Time Spent',

    // Tab Visit Events
    SELF_REVIEW_TAB_VISIT: 'MA - Self Review - Tab Visit',
    MANAGER_REVIEW_TAB_VISIT: 'MA - Prev Review - Tab Visit',
    ALL_FEEDBACK_TAB_VISIT: 'MA - All Feedback - Tab Visit',
    ORGANIZED_FEEDBACK_TAB_VISIT: 'MA - GPT Feedback - Tab Visit',
    REVIEW_GPT_TAB_VISIT: 'MA - ReviewGPT - Tab Visit',

    // Tab Time Spent Events
    SELF_REVIEW_TIME_SPENT: 'MA - Self Review - Time Spent',
    MANAGER_REVIEW_TIME_SPENT: 'MA - Prev Review - Time Spent',
    ALL_FEEDBACK_TIME_SPENT: 'MA - All Feedback - Time Spent',
    ORGANIZED_FEEDBACK_TIME_SPENT: 'MA - GPT Feedback - Time Spent',
    REVIEW_GPT_TIME_SPENT: 'MA - ReviewGPT - Time Spent',

    // Review GPT Events
    REVIEW_GPT_GENERATE_PDF: 'MA - ReviewGPT - Generate PDF',

    // Sidebar Events
    INDIRECT_REPORTS_SELECTED: 'MA - Indirect - Selected',
    SECONDARY_REPORTS_SELECTED: 'MA - Secondary - Selected',
    SPRITES_CYCLE_SELECTED: 'MA - Cycle - Selected',
  },

  FEEDBACK_ASSISTANT: {
    TIME_SPENT: 'FA - Time Spent',
    MESSAGE_SENT: 'FA - Message Sent',
    COPY_FEEDBACK: 'FA Chat - Copy Feedback',
  },

  CAREER_FRAMEWORKS: {
    TIME_SPENT: 'CF - Time Spent',
  },

  MODULE_FEEDBACK: {
    MANAGER_ASSISTANT_HELPFUL: 'MF - MA - Was Helpful',
    MANAGER_ASSISTANT_NOT_HELPFUL: 'MF - MA - Was Not Helpful',
    FEEDBACK_ASSISTANT_HELPFUL: 'MF - FA Chat - Was Helpful',
    FEEDBACK_ASSISTANT_NOT_HELPFUL: 'MF - FA Chat - Was Not Helpful',
    CAREER_FRAMEWORKS_HELPFUL: 'MF - CF - Was Helpful',
    CAREER_FRAMEWORKS_NOT_HELPFUL: 'MF - CF - Was Not Helpful',
  },
};

// Tab ID to Event Name Mapping
export const TAB_VISIT_EVENT_MAP: Record<string, string> = {
  self_review: ANALYTICS_EVENTS.MANAGER_ASSISTANT.SELF_REVIEW_TAB_VISIT,
  manager_review: ANALYTICS_EVENTS.MANAGER_ASSISTANT.MANAGER_REVIEW_TAB_VISIT,
  all_feedback: ANALYTICS_EVENTS.MANAGER_ASSISTANT.ALL_FEEDBACK_TAB_VISIT,
  organized_feedback:
    ANALYTICS_EVENTS.MANAGER_ASSISTANT.ORGANIZED_FEEDBACK_TAB_VISIT,
  review_gpt: ANALYTICS_EVENTS.MANAGER_ASSISTANT.REVIEW_GPT_TAB_VISIT,
};

// Tab ID to Time Spent Event Name Mapping
export const TAB_TIME_SPENT_EVENT_MAP: Record<string, string> = {
  self_review: ANALYTICS_EVENTS.MANAGER_ASSISTANT.SELF_REVIEW_TIME_SPENT,
  manager_review: ANALYTICS_EVENTS.MANAGER_ASSISTANT.MANAGER_REVIEW_TIME_SPENT,
  all_feedback: ANALYTICS_EVENTS.MANAGER_ASSISTANT.ALL_FEEDBACK_TIME_SPENT,
  organized_feedback:
    ANALYTICS_EVENTS.MANAGER_ASSISTANT.ORGANIZED_FEEDBACK_TIME_SPENT,
  review_gpt: ANALYTICS_EVENTS.MANAGER_ASSISTANT.REVIEW_GPT_TIME_SPENT,
};

// Types
export interface AnalyticsEventParams {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

/**
 * Helper function to send Google Analytics events with consistent structure
 * @param action - The event action name (should use ANALYTICS_EVENTS constants)
 * @param params - Additional parameters (category, label, value)
 */
export const sendAnalyticsEvent = (
  action: string,
  params?: {
    category?: string;
    label?: string;
    value?: number;
  }
) => {
  if (!_analyticsEnabled) return;
  ReactGA.event({
    category: params?.category || 'SPRiTEsGPT',
    action,
    label: params?.label || action,
    ...(params?.value !== undefined && {value: params.value}),
  });
};

/**
 * Helper function to send time spent events
 * @param action - The event action name for time spent
 * @param timeSpentSeconds - Time spent in seconds
 * @param category - Optional category override
 */
export const sendTimeSpentEvent = (
  action: string,
  timeSpentSeconds: number,
  category?: string
) => {
  sendAnalyticsEvent(action, {
    category: category || 'SPRiTEsGPT',
    label: action,
    value: timeSpentSeconds,
  });
};
